import type { Ref } from 'vue';
import {
  FieldHtmlNameUtil,
  FormIdUtil,
  type HekateDisplayForm,
  Trans,
  BuildTags,
} from '@onshape/www-inertia';
import hekateBus from '../../../../../../bus';
import AbstractSignupScreen from './AbstractSignupScreen';
import type { OnSubmitFormFunction } from '../../../../../../model/OnSubmitFormFunction';
import type UnifiedSignupScreenComponent from '../UnifiedSignupScreenComponent';
import type UnifiedSignupSessionInfo from '../session-info/UnifiedSignupSessionInfo';
import type { HekateSubmitFormLiteResult } from '../../../../../../model/HekateSubmitFormLiteResult';
import ErrorUtil from '../../../../../util/ErrorUtil';

export default abstract class AbstractSubmitSignupScreen extends AbstractSignupScreen {
  private readonly onSubmitForm: OnSubmitFormFunction;

  protected readonly otherScreenComponents: UnifiedSignupScreenComponent[];

  private readonly isFormSubmitting: Ref<boolean>;

  private readonly onUpdatePage: VoidFunction;

  constructor({
    sessionInfo,
    formDefinition,
    onSubmitForm,
    isBackButtonHidden,
    otherScreenComponents,
    isFormSubmitting,
    onUpdatePage,
  }: {
    sessionInfo: Ref<UnifiedSignupSessionInfo>;
    formDefinition: Ref<HekateDisplayForm>;
    onSubmitForm: OnSubmitFormFunction;
    isBackButtonHidden: Ref<boolean>;
    otherScreenComponents: UnifiedSignupScreenComponent[];
    isFormSubmitting: Ref<boolean>;
    onUpdatePage: VoidFunction;
  }) {
    super({ sessionInfo, formDefinition, isBackButtonHidden });
    this.onSubmitForm = onSubmitForm;
    this.otherScreenComponents = otherScreenComponents;
    this.isFormSubmitting = isFormSubmitting;
    this.onUpdatePage = onUpdatePage;
  }

  /** The components that are visible if there are no recaptcha errors */
  abstract getThisScreensVisibleComponents(): UnifiedSignupScreenComponent[];

  public getVisibleComponents(): UnifiedSignupScreenComponent[] {
    const { formSubmitStatus } = this.sessionInfo.value;

    if (formSubmitStatus !== 'submit-failed') {
      this.otherScreenComponents.forEach((cmp) => {
        cmp.displayField.componentName = 'hidden-field';
      });
    } else {
      this.otherScreenComponents.forEach((cmp) => {
        cmp.displayField.componentName = cmp.displayField.initialComponentName;
      });
    }

    const result = [...this.otherScreenComponents, ...this.getThisScreensVisibleComponents()];

    return result;
  }

  public getValidatedComponents(): UnifiedSignupScreenComponent[] {
    return this.getVisibleComponents().filter(
      (cmp) => cmp.displayField.displayType !== 'form-field-group'
    );
  }

  public async toNextScreen(): Promise<boolean> {
    this.getField(FieldHtmlNameUtil.ussPath).val = AbstractSignupScreen.signupPath.join(' ');
    this.getField(FieldHtmlNameUtil.telemetryHekateBuild).val = BuildTags.packageVersion;
    this.getField(FieldHtmlNameUtil.approveUser).val = 'yes';
    this.getField(FieldHtmlNameUtil.planId).val = this.sessionInfo.value.chosenPlan!.planId;
    this.getField(FieldHtmlNameUtil.isEduRenew).val = this.sessionInfo.value.isEduRenew;
    this.getField(FieldHtmlNameUtil.isProUpgrade).val = this.sessionInfo.value.isProUpgrade;

    this.isFormSubmitting.value = true;

    // we need this to make sure the http request goes out before we go to the
    // next screen and start polling
    let submitStarted!: (_value: null) => void;
    const startedPromise = new Promise<null>((res) => {
      submitStarted = res;

      hekateBus.addEventListener('form-submit-http-request-started', (_evt) => {
        submitStarted(null);
      });
    });

    await Promise.race([this.runSubmit(), startedPromise]);

    return true;
  }

  private async runSubmit() {
    this.sessionInfo.value.formSubmitStatus = 'submitting';
    hekateBus.addEventListener('form-successfully-submitted', async (evt) => {
      const resolution = await evt.data.formSubmitResolution;
      this.sessionInfo.value.resolutionActions = resolution.successActions;
    });

    try {
      const submitPromise = this.onSubmitForm(new Event('uss-submit-signup'));
      const submitFormResult = await Promise.race([
        submitPromise,
        AbstractSubmitSignupScreen.timeOutRacer(),
      ]);

      if (submitFormResult.statusCode === 9000) {
        ErrorUtil.safePostMessageToSentry({
          tag: 'HekateUnifiedSignup',
          msg: 'Form submission timed out',
        });

        // last ditch effort to catch the form submission result if it
        // eventually comes back after the timeout - we don't want users to be
        // stuck on the submitting screen if the form submission eventually
        // succeeds
        hekateBus.addEventListener('form-successfully-submitted', async (evt) => {
          const isUnifiedSignupForm = FormIdUtil.isUnifiedSignupForm({
            formId: evt.data.formDefinition.id,
          });

          if (!isUnifiedSignupForm) {
            return;
          }

          ErrorUtil.safePostMessageToSentry({
            tag: 'HekateUnifiedSignup',
            msg: 'Form submission eventually succeeded after timeout',
          });

          this.sessionInfo.value.formSubmitStatus = 'submitted';
          this.sessionInfo.value.completedDetailsScreens.push(this.screenName);
          this.sessionInfo.value.submitFormResult = {
            isSuccess: true,
            statusCode: 201,
          };
          this.onUpdatePage();
        });
      }

      if (!submitFormResult.isSuccess) {
        this.sessionInfo.value.formSubmitStatus = 'submit-failed';
      } else {
        this.sessionInfo.value.formSubmitStatus = 'submitted';
        this.sessionInfo.value.completedDetailsScreens.push(this.screenName);
        this.sessionInfo.value.submitFormResult = submitFormResult;
      }
    } catch (e) {
      console.warn(e);
      this.sessionInfo.value.formSubmitStatus = 'submit-failed';
    } finally {
      this.isFormSubmitting.value = false;
      // force reactivity update
      this.sessionInfo.value = { ...this.sessionInfo.value };

      console.log(
        'Form submission completed with status:',
        this.sessionInfo.value.formSubmitStatus
      );
    }
  }

  /**
   * Timeout racer for form submission - if the form submission takes too long,
   * we want to fail gracefully
   */
  private static timeOutRacer() {
    const timeoutSeconds = 45;
    return new Promise<HekateSubmitFormLiteResult>((res) => {
      setTimeout(
        () =>
          res({
            isSuccess: false,
            statusCode: 9000,
          }),
        timeoutSeconds * 1000
      );
    });
  }

  public override getNextButtonLabel(): string {
    return Trans.byValue('Complete Sign up');
  }

  public override isSubmitScreen(): boolean {
    return true;
  }

  public getProgress(): number {
    return 85;
  }
}
